import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

/* =================================================================
   Company Hub — Prontuário Corporativo SST
   Casca compartilhada de TODAS as sub-rotas /app/empresas/:id/*
   Provê: header premium · 7 KPIs · menu contextual · breadcrumb.
   ================================================================= */

const TABS = [
  { to: "dashboard",   label: "Dashboard",       icon: "fa-gauge-high" },
  { to: "avaliacoes",  label: "Avaliações",      icon: "fa-clipboard-list" },
  { to: "riscos",      label: "Riscos Psicossociais", icon: "fa-shield-halved" },
  { to: "pgr",         label: "PGR",             icon: "fa-file-shield" },
  { to: "plano-acao",  label: "Plano de Ação",   icon: "fa-list-check" },
  { to: "absenteismo", label: "Absenteísmo",     icon: "fa-user-clock" },
  { to: "documentos",  label: "Documentos",      icon: "fa-folder-tree" },
  { to: "downloads",   label: "Downloads",       icon: "fa-cloud-arrow-down" },
  { to: "relatorios",  label: "Relatórios",      icon: "fa-file-lines" },
  { to: "historico",   label: "Histórico",       icon: "fa-clock-rotate-left" },
  { to: "config",      label: "Configurações",   icon: "fa-gear" },
];

/* ---------- KPI Card (premium, minimal) ---------- */
function Kpi({ label, value, hint, icon, accent = "emerald", testid }) {
  const bg = {
    emerald: "from-emerald-50 to-white border-emerald-200",
    rose:    "from-rose-50 to-white border-rose-200",
    amber:   "from-amber-50 to-white border-amber-200",
    sky:     "from-sky-50 to-white border-sky-200",
    slate:   "from-slate-50 to-white border-slate-200",
  }[accent] || "from-emerald-50 to-white border-emerald-200";
  const ic = {
    emerald: "text-emerald-700 bg-emerald-100",
    rose:    "text-rose-700 bg-rose-100",
    amber:   "text-amber-700 bg-amber-100",
    sky:     "text-sky-700 bg-sky-100",
    slate:   "text-slate-700 bg-slate-100",
  }[accent];
  return (
    <div data-testid={testid} className={`bg-gradient-to-br ${bg} border p-4 flex flex-col gap-2`}>
      <div className="flex items-center justify-between">
        <div className="overline text-[10px] text-slate-500">{label}</div>
        <span className={`${ic} w-8 h-8 inline-flex items-center justify-center rounded-md`}>
          <i className={`fa-solid ${icon} text-sm`}></i>
        </span>
      </div>
      <div className="font-display text-2xl font-extrabold text-slate-900 leading-none">{value}</div>
      {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

/* ---------- Status pill (contratual) ---------- */
function StatusPill({ value }) {
  const map = {
    ativo:    "bg-emerald-100 text-emerald-800 border-emerald-300",
    pendente: "bg-amber-100 text-amber-800 border-amber-300",
    inativo:  "bg-rose-100 text-rose-800 border-rose-300",
  };
  const cls = map[(value || "").toLowerCase()] || "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <span className={`${cls} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>
      {value || "—"}
    </span>
  );
}

export default function CompanyHub() {
  const { id } = useParams();
  const loc = useLocation();
  const [company, setCompany] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, assessmentsRes, plansRes, downloadsRes] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get("/assessments").catch(() => ({ data: [] })),
        api.get("/action-plans").catch(() => ({ data: [] })),
        api.get("/admin/downloads").catch(() => ({ data: [] })),
      ]);
      setCompany(c);

      const myAssessments = (assessmentsRes.data || []).filter((a) => a.company_id === id);
      const myPlans       = (plansRes.data || []).filter((p) => p.company_id === id);
      const pendingPlans  = myPlans.filter((p) => p.status !== "concluido" && p.status !== "done");
      // Score médio dos riscos (média dos avg_score das avaliações concluídas)
      const scored = myAssessments.filter((a) => typeof a.global_score === "number");
      const avgScore = scored.length
        ? scored.reduce((s, a) => s + a.global_score, 0) / scored.length
        : null;

      setKpis({
        colaboradores: c.n_trabalhadores || c.colaboradores || 0,
        avaliacoes: myAssessments.length,
        risco_medio: avgScore,
        acoes_pendentes: pendingPlans.length,
        documentos_vencendo: 0,     // placeholder — entra em F3 (prontuário documental)
        treinamentos_pendentes: 0,  // idem
        conformidade_nr01: scored.length
          ? Math.round(100 - Math.min(100, ((avgScore || 0) / 4) * 100))
          : null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const activeTab = useMemo(() => {
    const slug = loc.pathname.split("/").pop();
    const found = TABS.find((t) => t.to === slug);
    return found || TABS[0];
  }, [loc.pathname]);

  if (loading) {
    return (
      <div className="p-12 text-center text-slate-500" data-testid="company-hub-loading">
        <i className="fa-solid fa-spinner fa-spin text-3xl mb-3"></i>
        <p className="text-sm">Carregando prontuário corporativo…</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-12 text-center" data-testid="company-hub-404">
        <i className="fa-solid fa-circle-exclamation text-3xl text-rose-500 mb-3"></i>
        <p className="text-slate-700">Empresa não encontrada.</p>
        <Link to="/app/empresas" className="btn btn-primary mt-4 inline-block">Voltar para Empresas</Link>
      </div>
    );
  }

  const cidadeUf = [company.cidade, company.uf].filter(Boolean).join("/");
  const fmtPct = (v) => v == null ? "—" : `${v}%`;
  const fmtScore = (v) => v == null ? "—" : v.toFixed(2);

  return (
    <div className="min-h-screen bg-slate-50/40">
      {/* ============ Breadcrumb ============ */}
      <div className="bg-white border-b border-slate-200 px-8 lg:px-10 py-3" data-testid="company-breadcrumb">
        <nav className="text-xs flex items-center gap-2 max-w-7xl mx-auto">
          <Link to="/app/empresas" className="text-slate-500 hover:text-emerald-800 inline-flex items-center gap-1.5">
            <i className="fa-solid fa-arrow-left"></i> Empresas
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-700 font-semibold truncate max-w-xs">{company.razao_social}</span>
          <span className="text-slate-300">/</span>
          <span className="text-emerald-800 font-semibold">{activeTab.label}</span>
        </nav>
      </div>

      {/* ============ Cabeçalho premium ============ */}
      <header className="bg-white border-b border-slate-200 px-8 lg:px-10 py-7" data-testid="company-hub-header">
        <div className="max-w-7xl mx-auto flex gap-6 items-start flex-wrap">
          <div className="w-20 h-20 flex-none border border-slate-200 bg-white overflow-hidden flex items-center justify-center">
            {company.logo_base64 ? (
              <img src={company.logo_base64} alt={company.razao_social} className="w-full h-full object-contain" />
            ) : (
              <i className="fa-solid fa-building text-3xl text-slate-300"></i>
            )}
          </div>
          <div className="flex-1 min-w-[300px]">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <div className="overline mb-1">Prontuário Corporativo SST</div>
                <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                  {company.razao_social}
                </h1>
                {company.nome_fantasia && (
                  <p className="text-slate-600 mt-1">{company.nome_fantasia}</p>
                )}
              </div>
              <Link
                to={`/app/empresas/${company.id}/prontuario`}
                className="btn btn-primary text-xs"
                data-testid="open-dossier"
                title="Gerar PDF executivo consolidado da empresa"
              >
                <i className="fa-solid fa-file-pdf"></i> Imprimir Prontuário
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-[12px] text-slate-600">
              {company.cnpj && <span><strong className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mr-1">CNPJ</strong><span className="font-mono">{company.cnpj}</span></span>}
              {company.cnae  && <span><strong className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mr-1">CNAE</strong><span className="font-mono">{company.cnae}</span></span>}
              {company.grau_risco && <span><strong className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mr-1">Grau</strong>{company.grau_risco}</span>}
              {cidadeUf && <span><strong className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mr-1">Local</strong>{cidadeUf}</span>}
              {(company.n_trabalhadores || company.colaboradores) && <span><strong className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mr-1">Colab.</strong>{company.n_trabalhadores || company.colaboradores}</span>}
              {company.responsavel_tecnico && <span><strong className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mr-1">Resp. Téc.</strong>{company.responsavel_tecnico}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <StatusPill value={company.status_contratual || "Ativo"} />
              {company.validade_documental && (
                <span className="text-[11px] text-slate-500"><i className="fa-regular fa-calendar mr-1"></i>Validade documental: <strong className="text-slate-700">{company.validade_documental}</strong></span>
              )}
            </div>
          </div>
        </div>

        {/* KPIs rápidos */}
        <div className="max-w-7xl mx-auto mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="company-hub-kpis">
          <Kpi testid="kpi-colaboradores"      label="Colaboradores"    value={kpis?.colaboradores || 0}     icon="fa-users"           accent="sky" />
          <Kpi testid="kpi-avaliacoes"         label="Avaliações"       value={kpis?.avaliacoes || 0}        icon="fa-clipboard-list"  accent="emerald" />
          <Kpi testid="kpi-risco-medio"        label="Risco médio"      value={fmtScore(kpis?.risco_medio)}  hint={kpis?.risco_medio ? "Escala 1-5" : ""} icon="fa-shield-halved"   accent={kpis?.risco_medio > 3 ? "rose" : kpis?.risco_medio > 2 ? "amber" : "emerald"} />
          <Kpi testid="kpi-acoes-pendentes"    label="Ações pendentes"  value={kpis?.acoes_pendentes || 0}   icon="fa-list-check"      accent={kpis?.acoes_pendentes > 0 ? "amber" : "slate"} />
          <Kpi testid="kpi-vencimentos"        label="Vencendo (30d)"   value={kpis?.documentos_vencendo}    hint="documentos" icon="fa-calendar-day"    accent="slate" />
          <Kpi testid="kpi-treinamentos"       label="Treinamentos"     value={kpis?.treinamentos_pendentes} hint="pendentes" icon="fa-graduation-cap"  accent="slate" />
          <Kpi testid="kpi-conformidade"       label="Conformidade NR-01" value={fmtPct(kpis?.conformidade_nr01)} icon="fa-circle-check"    accent={kpis?.conformidade_nr01 == null ? "slate" : kpis.conformidade_nr01 >= 80 ? "emerald" : kpis.conformidade_nr01 >= 60 ? "amber" : "rose"} />
        </div>
      </header>

      {/* ============ Menu contextual + conteúdo ============ */}
      <div className="max-w-7xl mx-auto px-8 lg:px-10 py-6 flex gap-6 items-start">
        <aside className="w-56 flex-none sticky top-6" data-testid="company-hub-nav">
          <nav className="bg-white border border-slate-200 overflow-hidden">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.to === "dashboard"}
                data-testid={`company-tab-${t.to}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "bg-emerald-50 border-emerald-700 text-emerald-900 font-semibold"
                      : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <i className={`fa-solid ${t.icon} w-4 text-center`}></i>
                <span className="truncate">{t.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Conteúdo da aba ativa — entregue via <Outlet/> filho */}
        <main className="flex-1 min-w-0" data-testid={`company-tab-content-${activeTab.to}`}>
          <Outlet context={{ company, kpis, reload: load }} />
        </main>
      </div>
    </div>
  );
}
